import { api } from '../../services/api'
import { useAuthStore } from '../../store/authStore'

/**
 * Client for Clip Factory, the separate clip service behind the Clips tab.
 *
 * Every call carries the signed-in user's FinCreator access token as Bearer,
 * read from the auth store the same way services/api.ts reads it; Clip
 * Factory checks it against FinCreator's /api/auth/me. Nothing is stored here.
 */

/** Read at call time, so the tab follows the build's value (and tests can change it). */
export const clipFactoryUrl = (): string | null => {
  const url = (process.env.REACT_APP_CLIP_FACTORY_URL || '').trim()
  return url ? url.replace(/\/+$/, '') : null
}

export type ClipFormat = 'vertical_speaker' | 'vertical' | 'original'

export interface PostCopy {
  titles: string[]
  caption: string
  hashtags: string[]
}

export interface Clip {
  start: number
  end: number
  duration: number
  title: string
  reason: string
  score: number
  text: string
  post_copy?: PostCopy
  /** Coin + direction pairs heard in the clip (e.g. "soul" heard as SOL). */
  mentions?: Mention[]
  /** The clip talks about a trade, so its caption carries "Not financial advice." */
  trade_talk?: boolean
  /** The range as the picker chose it, before any edge move. */
  picked?: { start: number; end: number }
  /** Edges moved to how you usually cut clips (seconds from the picked range), from your export history. */
  edges?: { start: number; end: number; exports: number }
  /** Added by Find clips: what it was found for ("the ETF", or "Trade calls"). */
  found_for?: string
}

/** A result said in a clip that FinCreator's posting rules flag (Clip Factory claims.py). */
export interface SpokenClaim {
  kind: string
  /** The words as said, e.g. "made 40%". */
  said: string
  /** Seconds into the clip as played. */
  time: number
  message: string
}

export interface ClaimsResult {
  /** False when FinCreator couldn't be asked; reason says why. */
  checked: boolean
  reason: string | null
  claims: SpokenClaim[]
}

export interface FindClipsResult {
  added: number
  /** Clips already in the list that the search also found. */
  already: number
  picker: 'claude' | 'heuristic'
  notice: string | null
  clips: VideoResults['clips']
  post_copy: Record<string, PostCopy>
}

/** Your clip history (Clip Factory history.py): what new suggestions' edges learn from. */
export interface ClipHistory {
  enabled: boolean
  exports: number
  videos: number
  min_exports: number
  min_videos: number
  /** The median shift applied to new suggestions; null until there's enough history. */
  edges: { start: number; end: number; exports: number; videos: number } | null
}

export interface Mention {
  coin: string
  direction: 'long' | 'short'
  heard: string
  time: number
}

/** One of the user's locked calls, as Clip Factory reads it from FinCreator. Every value is the record's own. */
export interface LockedCall {
  id: number
  symbol: string
  coin: string
  direction: 'long' | 'short'
  locked_at: string
  locked_at_text: string
  entry: number
  stop: number
  target: number
  entry_text: string
  stop_text: string
  target_text: string
  status: 'open' | 'closed' | 'cancelled'
  outcome: 'Win' | 'Loss' | 'Break-even' | null
  r: number | null
  result_text: string
  receipt_path: string
  card_text: string
  /** The receipt card's lines, as burned in. */
  card_lines?: string[]
}

/** The clips in your videos that match one of your calls (GET /calls/{id}/clips), newest video first. */
export interface CallClips {
  call: LockedCall
  clips: { file_id: string; filename: string; clip_key: string; title: string; start: number; end: number; uploaded_at: number }[]
}

export interface ClipCallMatch {
  mentions: Mention[]
  /** Calls whose coin and direction match a mention. Empty means no tag. */
  matches: LockedCall[]
  /** A trade is mentioned, the calls were looked up, and none matched. */
  no_receipt: boolean
}

export interface CallMatches {
  available: boolean
  reason: string | null
  clips: Record<string, ClipCallMatch>
}

export interface CallList {
  available: boolean
  reason: string | null
  calls: LockedCall[]
}

export interface ClipFactoryConfig {
  ai_available: boolean
  /** Clip Factory's feature flags; a feature that's off answers 404 and is hidden. */
  features?: Partial<Record<'episode_package' | 'audiograms' | 'speaker_names' | 'link_import' | 'zip_export' | 'transcript_corrections', boolean>>
  calls_available?: boolean
  create?: { voice_available: boolean; renders_per_hour: number; max_seconds: number; script_max_words: number }
}

export interface VideoSource {
  kind: 'link' | 'created'
  template?: 'call' | 'script'
  call_id?: number
}

/** What an export is made with, besides its range. */
/** How burned-in captions look (Clip Factory export.CAPTION_STYLES). */
export type CaptionStyle = 'classic' | 'highlight' | 'boxed'

export interface ExportOptions {
  format: ClipFormat
  captions: boolean
  /** Absent means classic. */
  caption_style?: CaptionStyle
  /** A real chart of a coin whose price is said (Clip Factory chart.py). */
  chart?: boolean
  watermark: boolean
  intro_outro: boolean
  hook: string | null
  receipt_call_id: number | null
}

export interface ExportRecord {
  id: string
  start: number
  end: number
  format: ClipFormat
  captions: boolean
  status: 'queued' | 'processing' | 'completed' | 'failed'
  progress?: number
  size_bytes?: number
  error?: string | null
  note?: string | null
  watermark?: boolean
  intro_outro?: boolean
  hook?: string | null
  receipt_call_id?: number | null
  receipt_text?: string | null
  cuts?: { start: number; end: number }[] | null
  caption_style?: CaptionStyle
  /** "Recorded by <date>", on a clip that says a price or a trade. */
  dated?: string | null
  charts?: ClipChart[] | null
  chart_note?: string | null
}

/** A coin's real chart, cut in while its price is said: closes up to the upload time. */
export interface ClipChart {
  coin: string
  /** When it shows: seconds into the clip on an export record, video time in a preview. */
  at: number
  until: number
  closes: number[]
  /** "BTC/USDT · 48h to Oct 29, 14:00 UTC · Kraken" */
  label: string
}

export interface ProcessStatus {
  file_id: string
  filename?: string
  status: 'queued' | 'processing' | 'completed' | 'failed'
  stage: 'downloading' | 'transcribing' | 'picking_clips' | 'rendering' | null
  progress: number
  error: string | null
  source?: VideoSource | null
}

export interface VideoResults extends ProcessStatus {
  filename: string
  size_bytes: number
  created_at: number
  expires_at: number | null
  has_video: boolean
  media_available: boolean
  transcription: { duration: number; segments: { start: number; end: number; text: string }[] }
  clips: { picker: 'claude' | 'heuristic'; model?: string | null; error?: string | null; clips: Clip[] } | null
  trims: Record<string, { start: number; end: number; cuts?: { start: number; end: number }[] }>
  post_copy: Record<string, PostCopy>
  exports: ExportRecord[]
  /** The saved episode package; null until one is made (or while the feature is off). */
  package?: EpisodePackage | null
}

/** Everything to publish with a whole episode (Clip Factory's package.py). */
export interface EpisodePackage {
  /** "claude": written from the full transcript. "quick": rule-based, with no show notes or description. */
  source: 'claude' | 'quick'
  created_at: number
  titles: string[]
  summary: string
  key_points: string[]
  chapters: { start: number; title: string }[]
  /** The chapters as YouTube wants them, one "0:00 Title" per line. */
  chapters_text: string
  youtube_description: string
  quotes: { start: number; text: string }[]
  hashtags: string[]
  notes: string[]
}

/** A coin name Whisper likely misheard in a caption line: characters [start, end) of its text. */
export interface CaptionFix {
  start: number
  end: number
  heard: string
  fix: string
}

/** One of a clip's caption lines (a transcript line), with its suggested fixes. */
export interface ClipLine {
  index: number
  start: number
  end: number
  text: string
  fixes: CaptionFix[]
}

export interface ClipLines {
  lines: ClipLine[]
  fix_count: number
}

/** A rectangle as fractions of the exported frame: x and w of its width, y and h of its height. */
export interface FrameRect {
  x: number
  y: number
  w: number
  h: number
}

/** Where "Follow the speaker" puts the 9:16 crop: [seconds in the video, crop centre 0..1 of its width]. */
export interface CropPath {
  path: [number, number][] | null
  /** Why there's no path: no faces, or the video is already narrow. */
  note: string | null
}

/** What an export would draw over the clip (POST /results/{id}/preview), for the live preview. */
export interface ClipOverlay {
  /** Caption chunks in video time; cut sections are skipped by the player. Words, for the highlight style. */
  captions: { start: number; end: number; text: string; words?: { start: number; end: number; text: string }[] }[]
  /** "Not financial advice." is drawn: the clip talks about a trade, or has a receipt card. */
  disclaimer: boolean
  /** The line drawn at the foot ("Recorded by Oct 29, 2026 · Not financial advice."), or null for none. */
  strip_text?: string | null
  /** With charts asked for: each chart in video time, and why one was left out. */
  charts?: ClipChart[]
  chart_note?: string | null
  frame: { width: number; height: number }
  hook: FrameRect
  watermark: FrameRect
  receipt: FrameRect
  disclaimer_strip: FrameRect
  /** font, outline and bottom as fractions of the frame's height; side of its width. */
  caption: { font: number; outline: number; bottom: number; side: number }
  hook_seconds: number
}

export interface VideoSummary {
  file_id: string
  filename: string
  status: ProcessStatus['status']
  stage: ProcessStatus['stage']
  error?: string | null
  created_at: number
  expires_at: number | null
  duration: number | null
  clip_count: number
  media_available: boolean
}

export interface LinkToken {
  param: string
  token: string
  expires_at: number
}

export class ClipFactoryError extends Error {
  status: number
  detail: string | null
  offset?: number

  constructor(status: number, message: string, detail: string | null = null, offset?: number) {
    super(message)
    this.status = status
    this.detail = detail
    this.offset = offset
  }
}

/** What to tell the user for a failed call. */
export const errorMessage = (status: number, detail: string | null): string => {
  if (status === 0) return "Can't reach Clip Factory. Check your connection and try again."
  if (status === 401) return 'Your FinCreator session expired. Log in again.'
  if (status === 403) return detail || 'Clips is only for FinCreator admins.'
  if (status === 503) return 'Clip Factory is unavailable right now. Try again in a minute.'
  if (status === 413) return detail || 'This file is too large.'
  if (status === 415) return detail || "This file type isn't supported."
  return detail || `Clip Factory returned an error (${status}).`
}

export const clipFactoryErrorText = (error: unknown, fallback: string): string =>
  error instanceof ClipFactoryError ? error.message : fallback

const bearer = (): Record<string, string> => {
  const token = useAuthStore.getState().token
  return token ? { Authorization: `Bearer ${token}` } : {}
}

/**
 * An expired access token makes Clip Factory answer 401. A FinCreator call
 * goes through services/api.ts, whose interceptor refreshes the token, so
 * one is made before retrying once.
 */
const refreshToken = async (): Promise<boolean> => {
  const before = useAuthStore.getState().token
  try {
    await api.get('/api/auth/me')
  } catch {
    return false
  }
  const after = useAuthStore.getState().token
  return !!after && after !== before
}

const readDetail = async (response: Response): Promise<{ detail: string | null; offset?: number }> => {
  try {
    const body = await response.json()
    return { detail: typeof body?.detail === 'string' ? body.detail : null, offset: body?.offset }
  } catch {
    return { detail: null }
  }
}

export const request = async <T>(path: string, init: RequestInit = {}, retried = false): Promise<T> => {
  const base = clipFactoryUrl()
  if (!base) throw new ClipFactoryError(0, "Clips isn't configured.")
  let response: Response
  try {
    response = await fetch(`${base}${path}`, {
      ...init,
      headers: { ...(init.body && typeof init.body === 'string' ? { 'Content-Type': 'application/json' } : {}),
        ...bearer(), ...(init.headers as Record<string, string> | undefined) },
    })
  } catch {
    throw new ClipFactoryError(0, errorMessage(0, null))
  }
  if (response.status === 401 && !retried && (await refreshToken())) {
    return request<T>(path, init, true)
  }
  if (!response.ok) {
    const { detail, offset } = await readDetail(response)
    throw new ClipFactoryError(response.status, errorMessage(response.status, detail), detail, offset)
  }
  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

const json = (body: unknown): RequestInit => ({ body: JSON.stringify(body) })

export const clipFactoryAPI = {
  myVideos: () => request<VideoSummary[]>('/my-videos'),
  status: (id: string) => request<ProcessStatus>(`/process/${id}`),
  results: (id: string) => request<VideoResults>(`/results/${id}`),
  linkToken: (id: string) => request<LinkToken>(`/results/${id}/link-token`, { method: 'POST' }),
  deleteVideo: (id: string) => request<void>(`/results/${id}`, { method: 'DELETE' }),
  saveTrim: (id: string, key: string, start: number, end: number, cuts: { start: number; end: number }[] = []) =>
    request<Record<string, { start: number; end: number }>>(`/results/${id}/trims/${encodeURIComponent(key)}`,
      { method: 'PUT', ...json(cuts.length ? { start, end, cuts } : { start, end }) }),
  words: (id: string, start: number, end: number) =>
    request<{ words: { start: number; end: number; word: string; filler: boolean }[] }>(
      `/results/${id}/words?start=${start}&end=${end}`),
  resetTrim: (id: string, key: string) =>
    request<Record<string, { start: number; end: number }>>(`/results/${id}/trims/${encodeURIComponent(key)}`,
      { method: 'DELETE' }),
  config: () => request<ClipFactoryConfig>('/config'),
  callMatches: (id: string) => request<CallMatches>(`/results/${id}/call-matches`),
  calls: () => request<CallList>('/calls'),
  draftScript: (topic: string) =>
    request<{ script: string; words: number }>('/create/draft', { method: 'POST', ...json({ topic }) }),
  createRender: (body: { template: 'call'; call_id: number } | { template: 'script'; script: string; voice: boolean }) =>
    request<ProcessStatus & { owner_key: string }>('/create/renders', { method: 'POST', ...json(body) }),
  startExport: (id: string, body: { start: number; end: number; cuts?: { start: number; end: number }[] } & ExportOptions) =>
    request<ExportRecord>(`/results/${id}/exports`, { method: 'POST', ...json(body) }),
  exportStatus: (id: string, exportId: string) => request<ExportRecord>(`/results/${id}/exports/${exportId}`),
  preview: (id: string, body: { start: number; end: number; cuts: { start: number; end: number }[]; format: ClipFormat; receipt: boolean; chart?: boolean; hook?: boolean }) =>
    request<ClipOverlay>(`/results/${id}/preview`, { method: 'POST', ...json(body) }),
  cropPath: (id: string, start: number, end: number) =>
    request<CropPath>(`/results/${id}/crop-path`, { method: 'POST', ...json({ start, end }) }),
  clipLines: (id: string, start: number, end: number) =>
    request<ClipLines>(`/results/${id}/clip-lines?start=${start}&end=${end}`),
  editClipLine: (id: string, index: number, text: string, start: number, end: number) =>
    request<{ index: number; text: string }>(`/results/${id}/clip-lines/${index}`, { method: 'PUT', ...json({ text, start, end }) }),
  fixClip: (id: string, start: number, end: number, key: string) =>
    request<ClipLines & { fixed: number; elsewhere: number }>(`/results/${id}/clip-fixes`,
      { method: 'POST', ...json({ start, end, key }) }),
  editPostCopy: (id: string, key: string, copy: PostCopy) =>
    request<PostCopy>(`/results/${id}/post-copy/${encodeURIComponent(key)}`, { method: 'PUT', ...json(copy) }),
  history: () => request<ClipHistory>('/history'),
  setHistory: (enabled: boolean) => request<ClipHistory>('/history', { method: 'PUT', ...json({ enabled }) }),
  clearHistory: () => request<ClipHistory>('/history', { method: 'DELETE' }),
  createPackage: (id: string) => request<EpisodePackage>(`/results/${id}/package`, { method: 'POST' }),
  clipsForCall: (callId: number) => request<CallClips>(`/calls/${callId}/clips`),
  /** A link that opens one finished export until `until` (Unix seconds) plus a margin, for a scheduled post. */
  publishLink: (id: string, exportId: string, until: number) =>
    request<{ url: string; expires_at: number }>(`/results/${id}/exports/${encodeURIComponent(exportId)}/publish-link`,
      { method: 'POST', ...json({ until }) }),
  findClips: (id: string, body: { request: string } | { trades: true }) =>
    request<FindClipsResult>(`/results/${id}/find-clips`, { method: 'POST', ...json(body) }),
  claims: (id: string, start: number, end: number, cuts: { start: number; end: number }[] = []) =>
    request<ClaimsResult>(`/results/${id}/claims`, { method: 'POST', ...json({ start, end, cuts }) }),
  rewritePostCopy: (id: string, key: string, start: number, end: number) =>
    request<PostCopy>(`/results/${id}/post-copy`, { method: 'POST', ...json({ key, start, end }) }),
}

/** A media or download URL, signed with a link token (they can't send a header). */
export const signedUrl = (id: string, path: string, token: LinkToken | null): string | null => {
  const base = clipFactoryUrl()
  if (!base || !token) return null
  return `${base}/results/${id}/${path}?${token.param}=${encodeURIComponent(token.token)}`
}

/**
 * One clip's caption file (SRT or WebVTT) as a signed download link, timed to
 * its export: 0 is the clip's first kept moment, cut sections are left out,
 * and with `intro` the lines start after the export's intro.
 */
export const clipCaptionsUrl = (
  id: string,
  range: { start: number; end: number; cuts?: { start: number; end: number }[] },
  format: 'srt' | 'vtt',
  intro: boolean,
  token: LinkToken | null
): string | null => {
  const url = signedUrl(id, 'clip-captions', token)
  if (!url) return null
  const params = new URLSearchParams({ start: String(range.start), end: String(range.end), format })
  if (range.cuts?.length) params.set('cuts', JSON.stringify(range.cuts.map(({ start, end }) => ({ start, end }))))
  if (intro) params.set('intro', 'true')
  return `${url}&${params}`
}

// ── Resumable upload ────────────────────────────────────────────────────────
// The file goes up in pieces, one request each. A piece that fails is retried
// for about two minutes; choosing the same file again later continues from
// where the server's copy ends. Only the upload's own id and key are kept in
// localStorage, to find it again.

const RESUME_KEY = 'clips-unfinished-uploads'
const RETRY_FOR_MS = 120_000

interface ResumeEntry {
  upload_id: string
  upload_key: string
}

const fileKey = (file: File) => `${file.name}:${file.size}:${file.lastModified}`

const readResume = (): Record<string, ResumeEntry> => {
  try {
    return JSON.parse(localStorage.getItem(RESUME_KEY) || '{}') || {}
  } catch {
    return {}
  }
}

const writeResume = (file: File, entry: ResumeEntry | null) => {
  try {
    const all = readResume()
    if (entry) all[fileKey(file)] = entry
    else delete all[fileKey(file)]
    localStorage.setItem(RESUME_KEY, JSON.stringify(all))
  } catch {
    // Storage blocked: the upload can't be continued after a reload, that's all.
  }
}

export interface UploadProgress {
  sent: number
  total: number
  bytesPerSecond: number
  retrying: boolean
}

const putPiece = (url: string, key: string, blob: Blob, onProgress: (loaded: number) => void) =>
  new Promise<number>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', url)
    Object.entries({ ...bearer(), 'X-Upload-Key': key, 'Content-Type': 'application/octet-stream' })
      .forEach(([name, value]) => xhr.setRequestHeader(name, value))
    xhr.upload.onprogress = (event) => onProgress(event.loaded)
    xhr.onerror = () => reject(new ClipFactoryError(0, errorMessage(0, null)))
    xhr.onload = () => {
      let body: { offset?: number; detail?: string } = {}
      try {
        body = JSON.parse(xhr.responseText || '{}')
      } catch {
        // not JSON
      }
      if (xhr.status >= 200 && xhr.status < 300 && typeof body.offset === 'number') resolve(body.offset)
      else {
        const detail = typeof body.detail === 'string' ? body.detail : null
        reject(new ClipFactoryError(xhr.status, errorMessage(xhr.status, detail), detail, body.offset))
      }
    }
    xhr.send(blob)
  })

const retryable = (error: unknown) =>
  error instanceof ClipFactoryError && (error.status === 0 || error.status === 429 || error.status >= 500)

const pause = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

/** Upload `file`, continuing an earlier attempt if there is one. Resolves to the new video's status. */
export const uploadFile = async (
  file: File,
  onProgress: (progress: UploadProgress) => void,
  signal: { cancelled: boolean }
): Promise<ProcessStatus> => {
  const base = clipFactoryUrl()
  if (!base) throw new ClipFactoryError(0, "Clips isn't configured.")

  let session: ResumeEntry | null = readResume()[fileKey(file)] || null
  let offset = 0
  let chunkSize = 8 * 1024 * 1024
  if (session) {
    try {
      const state = await request<{ offset: number; chunk_size: number; file_id: string | null }>(
        `/uploads/${session.upload_id}`, { headers: { 'X-Upload-Key': session.upload_key } })
      if (state.file_id) {
        writeResume(file, null)
        return clipFactoryAPI.status(state.file_id)
      }
      offset = state.offset
      chunkSize = state.chunk_size || chunkSize
    } catch {
      session = null // expired or cancelled: start again
    }
  }
  if (!session) {
    const started = await request<ResumeEntry & { chunk_size: number }>('/uploads',
      { method: 'POST', ...json({ filename: file.name, size: file.size }) })
    session = { upload_id: started.upload_id, upload_key: started.upload_key }
    chunkSize = started.chunk_size || chunkSize
    writeResume(file, session)
  }

  const began = Date.now()
  const startOffset = offset
  let lastSuccess = Date.now()
  let delay = 1000
  const report = (sent: number, retrying = false) => {
    const seconds = (Date.now() - began) / 1000
    onProgress({ sent, total: file.size, bytesPerSecond: seconds > 0 ? (sent - startOffset) / seconds : 0, retrying })
  }
  report(offset)

  while (offset < file.size) {
    if (signal.cancelled) throw new ClipFactoryError(0, 'Upload cancelled.')
    const piece = file.slice(offset, Math.min(offset + chunkSize, file.size))
    try {
      const from = offset
      offset = await putPiece(`${base}/uploads/${session.upload_id}?offset=${offset}`, session.upload_key, piece,
        (loaded) => report(from + loaded))
      lastSuccess = Date.now()
      delay = 1000
      report(offset)
    } catch (error) {
      if (error instanceof ClipFactoryError && error.status === 409 && typeof error.offset === 'number') {
        offset = error.offset // a piece that arrived but whose answer was lost
        continue
      }
      if (error instanceof ClipFactoryError && error.status === 401 && (await refreshToken())) continue
      if (!retryable(error) || Date.now() - lastSuccess > RETRY_FOR_MS) throw error
      report(offset, true)
      await pause(delay)
      delay = Math.min(delay * 2, 15_000)
    }
  }

  const job = await request<ProcessStatus>(`/uploads/${session.upload_id}/complete`,
    { method: 'POST', headers: { 'X-Upload-Key': session.upload_key } })
  writeResume(file, null)
  return job
}
